import React, { useEffect, useState } from "react";
import firebase from "../../firebase/firebaseConfig";
import { createCheckoutSession } from "../../services/subscriptionService";
import plansData from "../../utils/data/plansData";
import LoadingSpinner from "../../utils/helper/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import Toast from "../../utils/helper/Toast";
import { FaCheck } from "react-icons/fa";
import logo from "../../assets/dreamStreamAILogo.png";

// diagonal - background;
function SubscriptionPage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [userName, setUserName] = useState("");
  const [planType, setPlanType] = useState("");
  const [isLoading, setIsLoading] = useState({ id: 0, loadingState: false });
  const navigate = useNavigate();
  const [toastInfo, setToastInfo] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    firebase.auth().onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
        setUserName(user.displayName);
        const userRef = firebase.database().ref("users/" + user.uid);
        userRef.on("value", (snapshot) => {
          const userVal = snapshot.val();
          if (userVal) {
            console.log("userVal", userVal);
            setPlanType(userVal?.subscription?.planType || "");
          }
        });
      } else {
        setUserId("");
        setUserName("");
        setPlanType("");
      }
    });
  }, [userId]);

  const checkout = (plan, id) => {
    if (userId) {
      setIsLoading({ id: id, loadingState: true });
      createCheckoutSession(plan, userId)
        .then((response) => {
          const { session } = response.data;
          window.location = session.url;
          setIsLoading({ id: 0, loadingState: false });
        })
        .catch((error) => {
          console.error(error.response ? error.response.data : error.message);
          setIsLoading({ id: 0, loadingState: false });
        });
    } else {
      setToastInfo({
        show: true,
        message: "Please login first",
        type: "failure",
      });
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    }
  };

  const handleSignout = () => {
    firebase
      .auth()
      .signOut()
      .then(() => {
        setToastInfo({
          show: true,
          message: "Logged out successfully",
          type: "success",
        });
      })
      .catch((error) => {
        setToastInfo({
          show: true,
          message: "Something went wrong",
          type: "failure",
        });
      });
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center w-full min-h-screen bg-white">
        {/* Header */}
        <div className="flex justify-between items-center w-full px-6 lg:px-12 h-20 bg-white/80 backdrop-blur-sm fixed top-0 left-0 z-50 shadow-sm">
          <div className="flex items-center">
            <img
              src={logo}
              alt="DreamStream AI Logo"
              onClick={() =>
                (window.location.href =
                  "https://software-dev-consulting-site.vercel.app/")
              }
              className="h-12 lg:h-14 w-auto cursor-pointer"
            />
          </div>
          <div className="absolute left-1/2 transform -translate-x-1/2">
            <div className="text-base lg:text-xl font-bold text-black tracking-wide whitespace-nowrap">
              Choose Your Plan
            </div>
          </div>
          <div className="hidden lg:flex justify-center items-center gap-4">
            {!userId ? (
              <a
                href="/login"
                className="bg-gray-100 hover:bg-gray-200 px-6 py-2.5 rounded-full text-base lg:text-lg text-black font-semibold text-center transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Login
              </a>
            ) : (
              <div className="flex items-center space-x-6">
                <span className="text-black text-lg font-medium">
                  Welcome, {userName}
                </span>
                <button
                  onClick={() => handleSignout()}
                  className="bg-gray-100 hover:bg-gray-200 px-6 py-2.5 rounded-full text-base text-black font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
          <button
            className="text-black text-3xl lg:hidden hover:text-gray-600 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            &#9776;
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden flex flex-col items-center bg-white/80 backdrop-blur-sm pb-6 w-full shadow-sm fixed top-20 left-0 z-40">
            {!userId ? (
              <a
                href="/login"
                className="bg-gray-100 hover:bg-gray-200 px-6 py-2.5 rounded-full text-base text-black font-semibold my-3 text-center w-4/5 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Login
              </a>
            ) : (
              <>
                <div className="text-black text-lg my-3 font-medium">
                  Welcome, {userName}
                </div>
                <button
                  onClick={() => handleSignout()}
                  className="bg-gray-100 hover:bg-gray-200 px-6 py-2.5 rounded-full text-base text-black font-semibold w-4/5 transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Logout
                </button>
              </>
            )}
          </div>
        )}

        {/* Main Content */}
        <div className="w-full pt-20">
          {/* Plans Grid */}
          <div className="container mx-auto px-6 pt-12 lg:pt-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-[900px] w-full mx-auto">
              {plansData.map((item) => (
                <div key={item.id} className="col-span-1 mx-auto w-full">
                  <div
                    className={`relative h-full rounded-[20px] border border-gray-200 bg-white shadow-sm px-8 py-10 w-full flex flex-col text-center ${
                      planType === item.name.toLowerCase()
                        ? "ring-2 ring-blue-500"
                        : ""
                    }`}
                  >
                    <div>
                      <h3 className="text-center text-2xl font-bold mb-2 text-gray-900">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-base mb-8">
                        {item.description}
                      </p>
                      <div className="mb-8">
                        <div className="flex items-center justify-center text-center mb-1">
                          <span className="text-[40px] font-bold text-gray-900">
                            ${item.price}
                          </span>
                        </div>
                        <span className="text-gray-600">per month</span>
                      </div>
                      <ul className="mb-12 space-y-4 text-left">
                        {item?.features?.map((feature, index) => (
                          <li
                            key={index}
                            className="flex items-start space-x-3"
                          >
                            <div className="rounded-full bg-[#4285f4] mt-1 flex-shrink-0 w-5 h-5 flex items-center justify-center">
                              <FaCheck className="text-white text-xs" />
                            </div>
                            <span className="text-gray-600 text-[15px]">
                              {feature.text}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-auto">
                      {planType === item.name.toLowerCase() ? (
                        <button
                          disabled
                          className="bg-[#4285f4] text-white rounded-full text-base px-8 py-3 font-medium w-full hover:bg-blue-600 transition-colors"
                        >
                          Current Plan
                        </button>
                      ) : (
                        <button
                          onClick={() => checkout(Number(item.price), item.id)}
                          className="bg-[#4285f4] text-white rounded-full text-base px-8 py-3 font-medium w-full hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                          disabled={
                            isLoading.id === item.id && isLoading.loadingState
                          }
                        >
                          {isLoading.id === item.id &&
                          isLoading.loadingState ? (
                            <LoadingSpinner />
                          ) : (
                            "Get started"
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Toast
        show={toastInfo.show}
        message={toastInfo.message}
        type={toastInfo.type}
      />
    </>
  );
}

export default SubscriptionPage;
